import { api } from './api';

export interface ProgramTemplate {
  id: string;
  title: string;
  pmk_template_id: string;
}

export interface ServiceItem {
  name: string;
  description?: string;
}

export interface Program {
  id: string;
  title: string;
  subtitle: string;
  description?: string;
  inclusions?: string;
  includes_services?: ServiceItem[] | null;
  target: string;
  medication_count?: number;
  medications?: Medication[];
  program_templates?: ProgramTemplate[];
  icon?: string;
  color?: string;
  image_key?: string;
  image_url?: string;
  program_price?: number;           // in cents
  clinic_facilitation_fee?: number; // in cents
  created_at: string;
  display_mode?: 'medication_select' | 'protocol_showcase';
  protocol_label?: string;
  program_options?: ProgramOption[];
}


export interface ProgramOption {
  id: string;
  label: string;
  description?: string;
  program_price: number;
  clinic_facilitation_fee: number;
  includes_services?: string[];
  is_subscription: boolean;
  subscription_frequency?: 'weekly' | 'monthly' | 'quarterly';
  sort_order: number;
}

export interface Medication {
  id: string;
  name: string;
  dose?: string; // Keep for backward compatibility
  doses: string[]; // New array of available doses
  sig: string[]; // Array of prescription instructions
  quantity?: string;
  description?: string;
  savings?: number;
  unit_of_measure?: string;
  product_id?: string;
  pharmetika_id?: string;
  map_dose_to_product?: {
    dose: string;
    price: number;
    base_price: string;
    product_id: string;
    compounding_reason?: string | null;
    product_type?: string | null;
  }[];
  template_id?: string | null;
  image_url?: string;
  product_type?: string | null;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface ProgramsResponse {
  programs: Program[];
  pagination: PaginationInfo;
}

export interface ProgramDetailsResponse {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  target: string;
  medications: Medication[];
  icon: string;
  color: string;
  image_key?: string;
  image_url?: string;
  includes_services?: ServiceItem[] | null;
  program_price?: number;           // in cents
  clinic_facilitation_fee?: number; // in cents
  created_at: string;
  display_mode?: 'medication_select' | 'protocol_showcase';
  protocol_label?: string;
  program_options?: ProgramOption[];
}

export interface SearchResponse extends ProgramsResponse {
  searchQuery: string;
}

export interface StorefrontApiOptions {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  clinicId?: string;
  displayMode?: 'medication_select' | 'protocol_showcase';
  signal?: AbortSignal;
}

/**
 * Get all programs with optional filtering and pagination
 */
export const getPrograms = async (
  options: StorefrontApiOptions = {}
): Promise<ProgramsResponse> => {
  const params = new URLSearchParams();

  if (options.page) params.append('page', options.page.toString());
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.search) params.append('search', options.search);
  if (options.clinicId) params.append('clinicId', options.clinicId);
  if (options.displayMode) params.append('displayMode', options.displayMode);

  const response = await api.get(`/storefront/programs?${params.toString()}`, {
    signal: options.signal,
  });
  return response.data?.data || response.data;
};

/**
 * Get program details by ID
 */
export const getProgramDetails = async (
  programId: string,
  clinicId?: string,
  signal?: AbortSignal,
  patientId?: string
): Promise<ProgramDetailsResponse> => {
  const params = new URLSearchParams();
  if (clinicId) params.append('clinicId', clinicId);
  if (patientId) params.append('patientId', patientId);
  const queryString = params.toString();

  const response = await api.get(`/storefront/programs/${programId}${queryString ? `?${queryString}` : ''}`, {
    signal,
  });
  return response.data;
};

/**
 * Search programs by query
 */
export const searchPrograms = async (
  query: string,
  options: StorefrontApiOptions = {}
): Promise<SearchResponse> => {
  const params = new URLSearchParams();
  params.append('q', query);

  if (options.page) params.append('page', options.page.toString());
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.clinicId) params.append('clinicId', options.clinicId);

  const response = await api.get(`/storefront/programs/search?${params.toString()}`);
  return response.data.data;
};

/**
 * Default medication from API
 */
export interface DefaultMedication {
  id: string;
  program_id: string;
  medication_id: string;
  medication_name: string;
  dose: string;
  sig: string;
  quantity: number;
  days_supply: string;
  refills_authorized: number;
  product_id: string;
  unit_of_measure: string;
}

/**
 * Get default medications for a program
 */
export const getProgramDefaultMedications = async (
  programId: string,
  clinicId?: string,
  signal?: AbortSignal,
  patientId?: string
): Promise<DefaultMedication[]> => {
  const params = new URLSearchParams();
  if (clinicId) params.append('clinicId', clinicId);
  if (patientId) params.append('patientId', patientId);

  const queryString = params.toString();
  const response = await api.get(
    `/storefront/programs/${programId}/default-medications${queryString ? `?${queryString}` : ''}`,
    { signal }
  );

  return response.data?.defaultMedications || response.data?.data?.defaultMedications || [];
};

/**
 * Get icon component name from API response
 */
export const getIconComponent = (iconName = 'Activity'): string => {
  const iconMap: { [key: string]: any } = {
    Activity: () => import('lucide-react').then(m => m.Activity),
    Heart: () => import('lucide-react').then(m => m.Heart),
    Brain: () => import('lucide-react').then(m => m.Brain),
    Users: () => import('lucide-react').then(m => m.Users),
    Moon: () => import('lucide-react').then(m => m.Moon),
    Baby: () => import('lucide-react').then(m => m.Baby),
    Package: () => import('lucide-react').then(m => m.Package),
  };

  // If the icon exists, return it. Otherwise, pick a random icon from the map.
  if (iconMap[iconName]) {
    return iconMap[iconName];
  } else {
    const iconKeys = Object.keys(iconMap);
    const randomKey = iconKeys[Math.floor(Math.random() * iconKeys.length)];
    return iconMap[randomKey];
  }
};
